// Ann02 (supervision) — per-client pinned-announcement banner.
//
// A "cannot-miss" amber banner surfaced on the client / review / bookkeeping surfaces. Each ACTIVE
// announcement is its own warning MessageBar so multiple pins stack. Anyone (reviewer, bookkeeper,
// preparer) can add one (small dialog) or resolve one (records who/when + an optional note).
// Reads/writes are open to all staff — the server enforces the same.
//
// Fluent tokens only (no hardcoded hex) so it stays theme-correct; the 📌 marker is an emoji per
// the design spec. Dialog surfaces are Fluent Dialog (not the controlled-Dropdown gotcha).

import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Field,
  Textarea,
  RadioGroup,
  Radio,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  MessageBarActions,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { LoadFailed } from './LoadFailed';
import { Add16Regular } from '@fluentui/react-icons';
import { useTranslation } from '../../i18n';
import { formatInstant } from '../../i18n/dates';
import {
  useClientAnnouncements,
  useCreateClientAnnouncement,
  useResolveClientAnnouncement,
  type ClientAnnouncement,
  type ClientAnnouncementKind,
} from '../../api/clientAnnouncements';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalM,
  },
  bar: {
    // Give the pinned bars extra visual weight — a thicker leading accent than the default.
    borderLeftWidth: tokens.strokeWidthThick,
  },
  meta: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalXXS,
  },
  addRow: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  dialogFields: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
});

const KIND_KEYS: Record<ClientAnnouncementKind, string> = {
  'review-with-client': 'announcements.kind.review-with-client',
  info: 'announcements.kind.info',
};

function formatDate(iso: string): string {
  try {
    return formatInstant(iso, 'dayYear');
  } catch {
    return iso;
  }
}

export function ClientAnnouncementBanner({ companyId }: { companyId: string | undefined }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useClientAnnouncements(companyId);
  const create = useCreateClientAnnouncement(companyId);
  const resolve = useResolveClientAnnouncement(companyId);

  const [addOpen, setAddOpen] = useState(false);
  const [text, setText] = useState('');
  const [kind, setKind] = useState<ClientAnnouncementKind>('review-with-client');

  // Resolve flow: the announcement being resolved + an optional note.
  const [resolving, setResolving] = useState<ClientAnnouncement | null>(null);
  const [note, setNote] = useState('');

  // Without a client context there is nothing to pin to.
  if (!companyId) return null;

  const active = (data?.items ?? []).filter((a) => a.isActive);

  function resetAdd() {
    setText('');
    setKind('review-with-client');
    setAddOpen(false);
  }

  function submitAdd() {
    const trimmed = text.trim();
    if (!trimmed) return;
    create.mutate(
      { text: trimmed, kind },
      { onSuccess: resetAdd }
    );
  }

  function submitResolve() {
    if (!resolving) return;
    const trimmed = note.trim();
    resolve.mutate(
      { id: resolving.id, resolutionNote: trimmed || undefined },
      {
        onSuccess: () => {
          setResolving(null);
          setNote('');
        },
      }
    );
  }

  // Don't flash an empty add-affordance while the first load is in flight.
  if (isLoading) {
    return (
      <div className={styles.root}>
        <Spinner size="tiny" label={t('announcements.loading')} labelPosition="after" />
      </div>
    );
  }

  // The pinned notes failed to load (P6, 2026-10-30): never an empty banner that reads as "no notes".
  if (error && !data) {
    return (
      <div className={styles.root} data-testid="client-announcement-banner">
        <MessageBar intent="error" className={styles.bar}>
          <MessageBarBody>
            <MessageBarTitle>{t('announcements.loadFailedTitle')}</MessageBarTitle>
            <LoadFailed onRetry={() => refetch()} />
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  return (
    <div className={styles.root} data-testid="client-announcement-banner">
      {active.map((a) => (
        <MessageBar key={a.id} intent="warning" className={styles.bar} data-testid="client-announcement">
          <MessageBarBody>
            <MessageBarTitle>📌 {KIND_KEYS[a.kind] ? t(KIND_KEYS[a.kind]) : t('announcements.fallbackKind')}</MessageBarTitle>
            <div>{a.text}</div>
            <div className={styles.meta}>
              {(a.authorName ?? 'PTC')} · {formatDate(a.createdAt)}
            </div>
          </MessageBarBody>
          <MessageBarActions>
            <Button
              size="small"
              appearance="secondary"
              onClick={() => {
                setResolving(a);
                setNote('');
              }}
            >
              {t('announcements.resolve')}
            </Button>
          </MessageBarActions>
        </MessageBar>
      ))}

      {create.isError && (
        <MessageBar intent="error">
          <MessageBarBody>{t('announcements.pinFailed')}</MessageBarBody>
        </MessageBar>
      )}
      {resolve.isError && (
        <MessageBar intent="error">
          <MessageBarBody>{t('announcements.resolveFailed')}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.addRow}>
        <Button
          size="small"
          appearance="subtle"
          icon={<Add16Regular />}
          onClick={() => setAddOpen(true)}
        >
          {t('announcements.add')}
        </Button>
      </div>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={(_, d) => (d.open ? setAddOpen(true) : resetAdd())}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('announcements.addTitle')}</DialogTitle>
            <DialogContent>
              <div className={styles.dialogFields}>
                <Field label={t('announcements.text')} required>
                  <Textarea
                    value={text}
                    onChange={(_, d) => setText(d.value)}
                    maxLength={4000}
                    rows={4}
                    placeholder={t('announcements.textPlaceholder')}
                  />
                </Field>
                <Field label={t('announcements.type')}>
                  <RadioGroup
                    value={kind}
                    onChange={(_, d) => setKind(d.value as ClientAnnouncementKind)}
                  >
                    <Radio value="review-with-client" label={t('announcements.kind.review-with-client')} />
                    <Radio value="info" label={t('announcements.kind.info')} />
                  </RadioGroup>
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={resetAdd}>
                {t('common.cancel')}
              </Button>
              <Button
                appearance="primary"
                disabled={!text.trim() || create.isPending}
                onClick={submitAdd}
              >
                {t('announcements.pin')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Resolve dialog */}
      <Dialog
        open={resolving !== null}
        onOpenChange={(_, d) => (d.open ? undefined : setResolving(null))}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('announcements.resolveTitle')}</DialogTitle>
            <DialogContent>
              <div className={styles.dialogFields}>
                <Field label={t('announcements.resolutionNote')}>
                  <Textarea
                    value={note}
                    onChange={(_, d) => setNote(d.value)}
                    maxLength={4000}
                    rows={3}
                    placeholder={t('announcements.resolutionPlaceholder')}
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setResolving(null)}>
                {t('common.cancel')}
              </Button>
              <Button appearance="primary" disabled={resolve.isPending} onClick={submitResolve}>
                {t('announcements.resolve')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
